import React, { useEffect, useState, useMemo, useCallback } from "react";
import Loader from "../../../../../../components/Loader";
import { useTranslation } from "react-i18next";

const formatDuration = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

export default function TypePostCallQueue({
    data, callQueue, people, setDataGraph,
    setFormattedHeader, user, filteredPosts
}) {
    const [groupPostSurvey, setGroupPostSurvey] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 15;
    const { t } = useTranslation();

    const structuredCalls = useMemo(() => {
        if (!data || data.length === 0) return [];

        if (data[0].calls) return data;

        const groups = new Map();
        data.flat(Infinity).forEach(call => {
            const cid = call.correlationid || 'unknown';
            if (!groups.has(cid)) groups.set(cid, { calls: [] });
            groups.get(cid).calls.push(call);
        });
        return Array.from(groups.values());
    }, [data]);



    const processData = useCallback(() => {
        if (!structuredCalls.length || !people) {
            setGroupPostSurvey({});
            setDataGraph([]);
            return;
        }

        const isHolding = user?.holdingId === 33;
        const callQueueValues = new Set(Object.values(callQueue || {}));

        const peopleMap = new Map();
        people.flat().forEach(p => {
            const fullName = `${p.firstName} ${p.lastName}`.trim();
            if (p.phone?.work) peopleMap.set(p.phone.work, fullName);
            if (p.phone?.mobile) peopleMap.set(p.phone.mobile, fullName);
        });

        const statsMap = new Map();
        const filterPostsSet = filteredPosts?.posts?.length ? new Set(filteredPosts.posts) : null;

        structuredCalls.forEach(group => {
            const segments = group.calls || [];
            const agentsHandledThisCall = new Map();

            segments.forEach(seg => {
                let agentKey = isHolding ? seg.calleridnumber : (seg.callednumber || seg.callingnumber);

                if (!agentKey || (isHolding && !callQueueValues.has(agentKey)) || (!isHolding && !peopleMap.has(agentKey))) return;
                if (filterPostsSet && !filterPostsSet.has(agentKey)) return;

                const d = parseInt(seg.duration || 0, 10);
                const r = parseInt(seg.ringduration || 0, 10);
                const isAns = seg.answerindicator === "Yes" || seg.answerindicator === "Yes-PostRedirection";

                if (!agentsHandledThisCall.has(agentKey)) {
                    agentsHandledThisCall.set(agentKey, { dur: d, ring: r, answered: isAns, count: 1 });
                } else {
                    const prev = agentsHandledThisCall.get(agentKey);
                    agentsHandledThisCall.set(agentKey, {
                        dur: prev.dur + d,
                        ring: Math.max(prev.ring, r), 
                        answered: prev.answered || isAns,
                        count: 1
                    });
                }
            });

            agentsHandledThisCall.forEach((agentStats, key) => {
                if (filteredPosts?.calltype === "Répondus" && !agentStats.answered) return;
                if (filteredPosts?.calltype === "Sans réponse" && agentStats.answered) return;

                if (!statsMap.has(key)) {
                    statsMap.set(key, {
                        total: 0, durationTotal: 0, response: 0, noresponse: 0, ringduration: 0, sumMaxRingForAvg: 0
                    });
                }

                const global = statsMap.get(key);
                global.total += 1;
                global.durationTotal += agentStats.dur;
                global.ringduration += agentStats.ring;

                if (agentStats.answered) {
                    global.response += 1;
                    global.sumMaxRingForAvg += agentStats.ring;
                } else {
                    global.noresponse += 1;
                }
            });
        });

        const finalResults = {};
        const reverseCallQueue = isHolding ? Object.fromEntries(Object.entries(callQueue).map(([k, v]) => [v, k])) : {};

        statsMap.forEach((s, key) => {
            const name = isHolding
                ? '⛔ ' + (reverseCallQueue[key] || '')
                : (peopleMap.get(key) || 'N/A');

            finalResults[key] = {
                Nom: name,
                Incoming: {
                    ...s,
                    averageduration: formatDuration(s.total ? s.durationTotal / s.total : 0),
                    averageresponse: formatDuration(s.response ? s.sumMaxRingForAvg / s.response : 0),
                    averageringduration: formatDuration(s.total ? s.ringduration / s.total : 0),
                    durationTotal: formatDuration(s.durationTotal),
                    ringduration: formatDuration(s.ringduration),
                    responsePercentage: s.total ? ((s.response / s.total) * 100).toFixed(2) + '%' : '0.00%'
                }
            };
        });

        setGroupPostSurvey(finalResults);

        const headers = [t("Poste"), t("Nom"), t("Nombre d'appels répondu"), t("Durée moyenne pour répondre"), t("Nombre d'appels non répondu"), t("Durée totale d'appels"), t("Durée moyenne d'appels"), t("Durée totale d'attente"), t("Durée moyenne d'attente"), t("Pourcentage de réponse")];
        const excelRows = [headers];
        
        const sortedKeysForGraph = Object.keys(finalResults).sort((a, b) => {
            const nameA = finalResults[a].Nom || "";
            const nameB = finalResults[b].Nom || "";
            return nameA.localeCompare(nameB, 'fr', { sensitivity: 'base' });
        });

        sortedKeysForGraph.forEach(k => {
            const row = finalResults[k];
            const i = row.Incoming;
            excelRows.push([k, row.Nom, i.response, i.averageresponse, i.noresponse, i.durationTotal, i.averageduration, i.ringduration, i.averageringduration, i.responsePercentage]);
        });
        
        setDataGraph(excelRows);
        setFormattedHeader(true);
    }, [structuredCalls, filteredPosts, user, callQueue, people, setDataGraph, setFormattedHeader, t]);

    useEffect(() => {
        processData();
        setCurrentPage(1);
    }, [processData]);

    const sortedAgentKeys = useMemo(() => {
        if (!groupPostSurvey) return [];
        return Object.keys(groupPostSurvey).sort((a, b) => {
            const nameA = groupPostSurvey[a].Nom || "";
            const nameB = groupPostSurvey[b].Nom || "";
            return nameA.localeCompare(nameB, 'fr', { sensitivity: 'base' });
        });
    }, [groupPostSurvey]);

    const currentAgents = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return sortedAgentKeys.slice(start, start + itemsPerPage);
    }, [sortedAgentKeys, currentPage]);

    const totalPages = Math.max(1, Math.ceil(sortedAgentKeys.length / itemsPerPage));

    if (!groupPostSurvey) return <Loader />;

    return (
        <div className="callQueuePostSurvey">
            <div className="pagination-callQueue-post">
                <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>{t("Précédent")}</button>
                <span>{t("Page")} {currentPage} {t("sur")} {totalPages}</span>
                <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}>{t("Suivant")}</button>
            </div>

            <div className="table-callqueue">
                <table className="callQueuePostSurvey-entrants">
                    <thead>
                        <tr>
                            <th>{t("Poste")}</th>
                            <th>{t("Nom")}</th>
                            <th>{t("Nombre d'appels répondu")}</th>
                            <th>{t("Durée moyenne pour répondre")}</th>
                            <th>{t("Nombre d'appels non répondu")}</th>
                            <th>{t("Durée totale d'appels")}</th>
                            <th>{t("Durée moyenne d'appels")}</th>
                            <th>{t("Durée totale d'attente")}</th>
                            <th>{t("Durée moyenne d'attente")}</th>
                            <th>{t("Taux de réponse")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentAgents.length > 0 ? (
                            currentAgents.map(agent => {
                                const data = groupPostSurvey[agent];
                                return (
                                    <tr key={agent}>
                                        <td>{agent}</td>
                                        <td>{data.Nom}</td>
                                        <td>{data.Incoming.response}</td>
                                        <td>{data.Incoming.averageresponse}</td>
                                        <td>{data.Incoming.noresponse}</td>
                                        <td>{data.Incoming.durationTotal}</td>
                                        <td>{data.Incoming.averageduration}</td>
                                        <td>{data.Incoming.ringduration}</td>
                                        <td>{data.Incoming.averageringduration}</td>
                                        <td>{data.Incoming.responsePercentage}</td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="10" style={{ textAlign: 'center', padding: '20px' }}>
                                    {t("Aucune donnée disponible pour ce filtre")}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}